/**
 * A árvore — o que o parser produz e o avaliador consome.
 *
 * ## A decisão que governa este arquivo (T2.2 do card)
 *
 * `prop("Valor")` vira `{tipo: 'prop', propertyId: '...'}` — **o id, não o nome**. A tradução
 * acontece uma única vez, no parser, contra o catálogo de colunas.
 *
 * O card marcou isso como *"decisão que precisa estar tomada ANTES da primeira linha"*, e o
 * Discovery achou o segundo motivo: `getRefType` (`core/expression/ref/ref.ts`) faz
 * `vars.find(v => v.id === ref.name)` — **o upstream inteiro já referencia coluna por id**.
 * Guardar por nome não seria só frágil a rename; seria a única parte do produto que faz
 * diferente.
 *
 * ⚠️ E o custo de mudar depois não é refatorar código — é **migrar dado de usuário**, porque
 * a expressão fica gravada no documento de quem a escreveu.
 *
 * Para EXIBIR de volta, `paraTexto()` recebe o catálogo e reconstrói `prop("Nome atual")`.
 * Renomear a coluna muda o que se lê sem tocar no que está gravado — que é o comportamento
 * inteiro que a decisão compra.
 */

export type No =
  | { tipo: 'numero'; valor: number }
  | { tipo: 'texto'; valor: string }
  | { tipo: 'booleano'; valor: boolean }
  | { tipo: 'prop'; propertyId: string; nomeOriginal: string }
  | { tipo: 'unario'; operador: '-' | 'not'; operando: No }
  | { tipo: 'binario'; operador: OperadorBinario; esquerda: No; direita: No }
  | { tipo: 'chamada'; nome: string; argumentos: No[]; posicao: number };

export type OperadorBinario =
  | '+' | '-' | '*' | '/'
  | '==' | '!=' | '>' | '<' | '>=' | '<=';

/** Todo `propertyId` que a expressão lê. É o insumo da detecção de ciclo (T2.3). */
export function propriedadesReferenciadas(no: No, saida = new Set<string>()): Set<string> {
  switch (no.tipo) {
    case 'prop':
      saida.add(no.propertyId);
      break;
    case 'unario':
      propriedadesReferenciadas(no.operando, saida);
      break;
    case 'binario':
      propriedadesReferenciadas(no.esquerda, saida);
      propriedadesReferenciadas(no.direita, saida);
      break;
    case 'chamada':
      no.argumentos.forEach(a => propriedadesReferenciadas(a, saida));
      break;
    default:
      break;
  }
  return saida;
}

/** Como o nome da coluna é escrito de volta no texto. */
function citar(nome: string): string {
  return `"${nome.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * A árvore de volta em texto, com os nomes ATUAIS das colunas.
 *
 * `nomePorId` devolve `undefined` para coluna que não existe mais. Nesse caso o texto sai com
 * um marcador visível em vez do id cru: o id não diz nada a quem lê, e some sem aviso seria
 * pior — o usuário precisa VER que a fórmula perdeu uma referência para poder consertá-la.
 */
export function paraTexto(no: No, nomePorId: (id: string) => string | undefined): string {
  switch (no.tipo) {
    case 'numero':
      return String(no.valor);
    case 'texto':
      return citar(no.valor);
    case 'booleano':
      return no.valor ? 'true' : 'false';
    case 'prop': {
      const nome = nomePorId(no.propertyId);
      return nome === undefined
        ? `prop(${citar(`⚠ coluna removida (${no.nomeOriginal})`)})`
        : `prop(${citar(nome)})`;
    }
    case 'unario':
      return no.operador === '-'
        ? `-${paraTexto(no.operando, nomePorId)}`
        : `not(${paraTexto(no.operando, nomePorId)})`;
    case 'binario':
      return `(${paraTexto(no.esquerda, nomePorId)} ${no.operador} ${paraTexto(no.direita, nomePorId)})`;
    case 'chamada':
      return `${no.nome}(${no.argumentos.map(a => paraTexto(a, nomePorId)).join(', ')})`;
    default:
      return '';
  }
}
